import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef } from "react";
import { Box } from "../Box/Box.mjs";
import { FieldOverlay } from "../private/FieldOverlay/FieldOverlay.mjs";
import { Text } from "../Text/Text.mjs";
import "../Text/TextContext.mjs";
import "../Heading/HeadingContext.mjs";
import "../../css/lineHeightContainer.css.mjs";
import { buildDataAttributes } from "../private/buildDataAttributes.mjs";
import "../../css/reset/reset.css.mjs";
import "../../css/atoms/sprinkles.css.mjs";
import "../../hooks/useIcon/icon.css.mjs";
import "../../css/typography.css.mjs";
import { IconDone } from "../icons/IconDone/IconDone.mjs";
import { virtualTouchable } from "../private/touchable/virtualTouchable.mjs";
import { useBackgroundLightness } from "../Box/BackgroundContext.mjs";
import { root, realField, realFieldPosition, fieldSize, slideContainer, slideContainerSize, slideTrack, slideTrackMask, slideTrackBgLightMode, slideTrackBgDarkMode, slideTrackSelected, slider, icon, focusOverlay, hoverOverlay, label } from "./Toggle.css.mjs";
import { Stack } from "../Stack/Stack.mjs";
const handleChange = (onChange) => (event) => {
  if (typeof onChange === "function") {
    onChange(event.target.checked);
  }
};
const Toggle = forwardRef(
  ({
    id,
    on,
    onChange,
    label: label$1,
    description,
    align = "left",
    size = "standard",
    data,
    disabled = false,
    ...restProps
  }, forwardedRef) => {
    const lightness = useBackgroundLightness();
    return /* @__PURE__ */ jsxs(
      Box,
      {
        position: "relative",
        zIndex: 0,
        display: "flex",
        alignItems: "center",
        flexDirection: align === "left" ? void 0 : "rowReverse",
        className: root,
        ...buildDataAttributes({ data, validateRestProps: restProps }),
        children: [
          /* @__PURE__ */ jsx(
            Box,
            {
              disabled,
              component: "input",
              type: "checkbox",
              id,
              checked: on,
              onChange: handleChange(onChange),
              position: "absolute",
              zIndex: 1,
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: 0,
              className: [realField, realFieldPosition[size], fieldSize[size]],
              ref: forwardedRef
            }
          ),
          /* @__PURE__ */ jsxs(
            Box,
            {
              position: "relative",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              className: [
                slideContainer,
                slideContainerSize[size],
                fieldSize[size]
              ],
              children: [
                /* @__PURE__ */ jsx(
                  Box,
                  {
                    position: "absolute",
                    width: "full",
                    overflow: "hidden",
                    borderRadius: "full",
                    className: [
                      slideTrack[size],
                      slideTrackMask,
                      slideTrackBgLightMode[lightness.lightMode],
                      slideTrackBgDarkMode[lightness.darkMode]
                    ],
                    children: /* @__PURE__ */ jsx(
                      Box,
                      {
                        position: "absolute",
                        width: "full",
                        height: "full",
                        background: "formAccent",
                        transition: "fast",
                        className: slideTrackSelected
                      }
                    )
                  }
                ),
                /* @__PURE__ */ jsxs(
                  Box,
                  {
                    position: "absolute",
                    background: disabled ? "disabled" : "surface",
                    boxShadow: "borderField",
                    transition: "fast",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "full",
                    className: slider[size],
                    children: [
                      /* @__PURE__ */ jsx(FieldOverlay, { className: icon, children: /* @__PURE__ */ jsx(IconDone, { tone: "formAccent", size: "fill" }) }),
                      /* @__PURE__ */ jsx(FieldOverlay, { variant: "focus", borderRadius: "full", className: focusOverlay }),
                      /* @__PURE__ */ jsx(FieldOverlay, { variant: "hover", borderRadius: "full", className: hoverOverlay })
                    ]
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx(
            Box,
            {
              component: "label",
              htmlFor: id,
              paddingLeft: align === "left" ? "xsmall" : void 0,
              paddingRight: align === "right" || align === "justify" ? "xsmall" : void 0,
              flexGrow: align === "justify" ? 1 : void 0,
              userSelect: "none",
              cursor: "pointer",
              className: [label[size], virtualTouchable()],
              children: /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
                /* @__PURE__ */ jsx(
                  Text,
                  {
                    tone: disabled ? "disabled" : void 0,
                    baseline: false,
                    weight: on ? "strong" : void 0,
                    size,
                    children: label$1
                  }
                ),
                description ? /* @__PURE__ */ jsx(Text, { tone: "tertiary", size: "xsmall", children: description }) : null
              ] })
            }
          )
        ]
      }
    );
  }
);
Toggle.displayName = "Toggle";
export {
  Toggle
};
